import { computed, signal } from "@preact/signals-react";
import { IEventV2 } from "@/app/dashboard/models/Event";
import { getEventForList } from "@/services/events-service";
import { ILocationV2 } from "@/app/dashboard/models/Location";
import { getLocationNames } from "@/services/locations-service";
import { getPath } from "@/lib/utils";
import { ICategoryV2 } from "@/app/dashboard/models/EventCategory";
import { getCategoryNames } from "@/services/category-service";
import { ICityV2 } from "@/app/dashboard/models/City";
import { getCityNames } from "@/services/cities-service";

export const eventList = signal<IEventV2[]>([]);
export const eventTotalCount = signal<number>(0);
export const searchText = signal<string>("");
export const cityNames = signal<ICityV2[]>([]);
export const displayCityNames = signal<ICityV2[]>([]);
export const locationNames = signal<ILocationV2[]>([]);
export const displayLocationNames = signal<ILocationV2[]>([]);
export const categoryNames = signal<ICategoryV2[]>([]);
export const selectedCities = signal<ICityV2[]>([]);
export const selectedLocations = signal<ILocationV2[]>([]);
export const selectedCategories = signal<ICategoryV2[]>([]);
export const selectedCityIds = computed(() =>
  selectedCities?.value.map((l) => l.id)
);
export const selectedLocationIds = computed(() =>
  selectedLocations?.value.map((l) => l.id)
);
export const selectedCategoryIds = computed(() =>
  selectedCategories?.value.map((l) => l.id)
);

export const selectedStartDate = signal<Date>();
export const setSelectedStartDate = (date: Date) =>
  (selectedStartDate.value = date);

export const selectedEndDate = signal<Date>();
export const setSelectedEndDate = (date: Date) =>
  (selectedEndDate.value = date);

export const setSearchText = (text: string) => (searchText.value = text);
export const offset = signal<number>(0);
export const limit = signal<number>(9);

export const totalPages = computed(() =>
  Math.ceil(eventTotalCount.value / limit.value)
);
export const currentPage = computed(
  () => Math.ceil(offset.value / limit.value) + 1
);
export const isFirstPage = computed(() => offset.value === 0);
export const isLastPage = computed(
  () => offset.value + limit.value >= eventTotalCount.value
);
export const isOpenFilterModal = signal<boolean>(false);
export const isAnyFittersSelected = signal<boolean>(false);

export const setIsAnyFitters = (isAnyFittersSelectedValue: boolean) => {
  isAnyFittersSelected.value = isAnyFittersSelectedValue;
};

export const setIsOpenModal = (isOpen: boolean) => {
  isOpenFilterModal.value = isOpen;
};

export const singleSelectedCategory = computed(() => {
  if (selectedCategories?.value?.length > 0) {
    console.log(selectedCategories?.value);
    return selectedCategories?.value[0].name;
  } else {
    return "";
  }
});

export const singleSelectedCity = computed(() => {
  if (selectedCities?.value?.length > 0) {
    console.log(selectedCities?.value);
    return selectedCities?.value[0].name;
  } else {
    return "";
  }
});

export const singleSelectedLocation = computed(() => {
  if (selectedLocations?.value?.length > 0) {
    console.log(selectedLocations?.value);
    return selectedLocations?.value[0].name;
  } else {
    return "";
  }
});

export const applyQueryToParams = (navigate: Function) => {
  const payload = {
    searchText: searchText.value,
    cityIds: selectedCityIds.value.join(","),
    locationIds: selectedLocationIds.value.join(","),
    categoryIds: selectedCategoryIds.value.join(","),
    startDate: selectedStartDate.value?.toISOString(),
    endDate: selectedEndDate.value?.toISOString(),
    offset: offset.value,
    limit: limit.value,
  };

  const clearedPrams = Object.entries(payload)
    .filter(([, value]) => !!value)
    .reduce((paramObject: any, item) => {
      const [key, value] = item;
      console.log(key, value);
      paramObject[key] = value;
      return paramObject;
    }, {});
  navigate(getPath("/events", clearedPrams));
  isOpenFilterModal.value = false;
};

export const clearSelectFilters = (navigate: Function) => {
  applyFormQueryParams();
  navigate(getPath("/events"));
};

export const applyFormQueryParams = (params?: any) => {
  searchText.value = params?.searchText ?? "";
  selectedCities.value = cityNames.value.filter((l) =>
    params?.cityIds?.includes(l.id)
  );
  selectedLocations.value = locationNames.value.filter((l) =>
    params?.locationIds?.includes(l.id)
  );
  selectedCategories.value = categoryNames.value.filter((l) =>
    params?.categoryIds?.includes(l.id)
  );

  if (params?.startDate) {
    selectedStartDate.value = new Date(params?.startDate);
  }
  if (params?.endDate) {
    selectedEndDate.value = new Date(params?.endDate);
  }
  if (params?.offset) {
    offset.value = parseInt(params?.offset);
  }
  if (params?.limit) {
    limit.value = parseInt(params?.limit);
  }
};

export const loadEventList = async () => {
  const payload = {
    searchText: searchText.value,
    cityIds: selectedCityIds.value,
    locationIds: selectedLocationIds.value,
    categoryIds: selectedCategoryIds.value,
    startDate: selectedStartDate.value,
    endDate: selectedEndDate.value,
  };
  const [response, error] = await getEventForList(
    payload,
    offset.value,
    limit.value
  );
  if (error) {
    return;
  }
  eventList.value = response.list;
  eventTotalCount.value = response.count;
};

export const loadCities = async () => {
  const [response, error] = await getCityNames();
  if (error) {
    return;
  }

  cityNames.value = response.list;
  displayCityNames.value = response.list;
};

export const loadLocations = async () => {
  const [response, error] = await getLocationNames();
  if (error) {
    return;
  }

  locationNames.value = response.list;
  displayLocationNames.value = response.list;
};

export const loadCategories = async () => {
  const [response, error] = await getCategoryNames();
  if (error) {
    return;
  }

  categoryNames.value = response.list;
};

export const onSelectCities = async (cities: ICityV2[] = []) => {
  selectedCities.value = cities;
};

export const onSelectLocations = async (locations: ILocationV2[] = []) => {
  selectedLocations.value = locations;
};

export const onSelectSingleCityById = async (cityId: string) => {
  const location = locationNames.value.filter((l) => l?.city?.id === cityId);
  displayLocationNames.value = location;
  const city = cityNames.value.find((l) => l.id === cityId);
  selectedCities.value = [city];
};
export const onSelectSingleLocationById = async (locationId: string) => {
  const city = cityNames.value.filter((city) =>
    city.locations?.some((location) => location.id === locationId)
  );
  displayCityNames.value = city;
  const location = locationNames.value.find((l) => l.id === locationId);
  selectedLocations.value = [location];
};
export const onSelectSingleCategoryById = async (categoryId: string) => {
  const category = categoryNames.value.find((c) => c.id === categoryId);
  selectedCategories.value = [category];
};

export const onSelectCategories = async (categories: ICategoryV2[] = []) => {
  selectedCategories.value = categories;
};

export const handlePreviousPage = (navigate: Function) => {
  if (!isFirstPage.value) {
    offset.value -= limit.value;
    applyQueryToParams(navigate);
  }
};

export const handleNextPage = (navigate: Function) => {
  if (!isLastPage.value) {
    offset.value += limit.value;
    applyQueryToParams(navigate);
  }
};
